/**
 * Simulador de conectores — la lógica pura de la pantalla, separada de la
 * vista para poder probarla sin React ni red (como skills/telemetria.ts).
 *
 * Qué decide aquí y por qué:
 * - La CONSULTA que sale de los controles: un flujo pide un periodo
 *   cerrado [inicio, fin] (mes, trimestre o año) y quizá un desglose; un
 *   saldo pide una fecha. Es exactamente el `ConsultaSimulador` del
 *   contrato, así que lo que se ve en los controles es lo que viaja.
 * - Los periodos que se ofrecen son CERRADOS y relativos a hoy: el
 *   simulador prueba recetas contra datos que ya no se mueven, y el mes en
 *   curso daría cifras distintas en cada clic.
 * - Salvo con COBERTURA (el banco de escenarios, que es fijo): entonces se
 *   anclan a lo que cubren sus datos, no a hoy, porque la ventana relativa
 *   deja atrás al escenario cada mes (prueba en pre, 2026-10-02). Con
 *   cobertura de periodo fijo solo hay una opción, la tabla entera, y
 *   ningún desglose.
 * - El desglose nunca es más grueso que el periodo (un trimestre por años
 *   es una fila con la cifra del trimestre): lo que no cabe va apagado.
 * - «Editada» = texto distinto del de referencia (el del repo o, en el
 *   banco, el derivado que devolvió la última ejecución). Nada se guarda.
 * - Las dimensiones que se pueden añadir son las que la receta OFRECE
 *   (`ConceptAvailability.dimensions`); las del concepto que la receta no
 *   ofrece se enseñan marcadas, como en el prototipo. Con la receta
 *   editada no se sabe qué ofrece (el YAML lo lee la API), así que se
 *   pueden pedir todas las del concepto y el resultado dirá
 *   `missing_dimension` si no la sirve.
 * - Estado → etiqueta y tono: el idioma del prototipo (simulador.html).
 * - Cobertura (ADR-18): lo que el origen cubre de un concepto viene por
 *   concepto (`coverage`) o, con la API anterior, en el sobre
 *   (`cobertura`). Lo pedido fuera es `period_not_covered`, con lo que sí
 *   cubre; lo cubierto en parte llega `ok` con el periodo recortado. De
 *   ahí salen el aviso, el recorte y los controles de «pedir lo que sí
 *   cubre».
 */
import type { CoberturaConcepto, ConceptAvailabilityWire, ConceptoCatalogo, ConceptResultWire, ConsultaSimulador, EntornoBC } from "@skein/contracts";
import { ApiError } from "@skein/api-client";

export type Estado = ConceptResultWire["status"];
export type Granularidad = "month" | "quarter" | "year";
/** El tono de una insignia del sistema (Badge `tone`); sin tono = neutra. */
export type Tono = "ok" | "warn" | "danger" | "accent" | undefined;

// ── vocabulario de la pantalla (del prototipo) ─────────────────────────

export const NIVELES = [
  { id: "pulse", titulo: "1 · Pulso" },
  { id: "profitability", titulo: "2 · Rentabilidad" },
  { id: "working_capital_risk", titulo: "3 · Circulante y riesgo" },
] as const;

const DIM: Readonly<Record<string, string>> = {
  customer: "Cliente", product_line: "Línea de producto", salesperson: "Comercial", employee: "Empleado", account: "Cuenta",
  bucket: "Tramo", vendor: "Proveedor", cost_center: "Centro de coste", item: "Artículo",
  project: "Proyecto", contract: "Contrato", work_type: "Tipo de trabajo", job_category: "Categoría", contract_type: "Tipo de contrato", work_mode: "Modalidad", channel: "Canal", reason: "Motivo", bank: "Entidad", instrument: "Instrumento", due_period: "Vencimiento",
};
/** «Cliente» para `customer`; el identificador tal cual si el catálogo trae uno nuevo. */
export const dimEtiqueta = (d: string): string => DIM[d] ?? d;
const TRAMO: Readonly<Record<string, string>> = { current: "No vencido", "1-30": "1–30 días", "31-60": "31–60 días", ">60": "Más de 60 días" };
export const tramoEtiqueta = (v: string): string | null => TRAMO[v] ?? null;

export const GRAN: Readonly<Record<Granularidad, string>> = { month: "Mes", quarter: "Trimestre", year: "Año" };

/** Lo que el prototipo abre por defecto al elegir el concepto (si la receta lo ofrece). */
const DIMS_POR_DEFECTO: Readonly<Record<string, readonly string[]>> = {
  revenue: ["customer"], accounts_receivable_aging: ["bucket"], accounts_payable_aging: ["bucket"], open_orders: ["customer"],
};

// ── fechas y periodos ──────────────────────────────────────────────────

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const MES_CORTO = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const pad = (n: number) => String(n).padStart(2, "0");
/** YYYY-MM-DD de una fecha LOCAL (no UTC: «hoy» es el de quien mira). */
export const isoLocal = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const finDeMes = (y: number, m0: number): string => isoLocal(new Date(y, m0 + 1, 0));
const ordinalTrimestre = (q: number) => `${q}.${q === 1 || q === 3 ? "er" : "º"} trimestre`;

/**
 * Un periodo del desplegable. `sub`: la sublínea, sin fechas ISO («oct – dic», «ene – ago»); el mes no la lleva.
 * `marca`: el periodo cae fuera de lo que cubre el origen, o solo en parte (ver `periodos`).
 */
export interface PeriodoOpcion { valor: string; etiqueta: string; inicio: string; fin: string; sub?: string; marca?: "fuera" | "parte" }
export const MARCA: Readonly<Record<"fuera" | "parte", string>> = { fuera: "fuera de lo que cubre", parte: "cubierto en parte" };

/** Lo que cubre el origen para un concepto (solo el banco); sin ella, los periodos son relativos a hoy. */
export type Cobertura = CoberturaConcepto;

const mesDe = (iso: string): number => Number(iso.slice(5, 7)) - 1;
const rangoMeses = (inicio: string, fin: string): string => `${MES_CORTO[mesDe(inicio)] ?? ""} – ${MES_CORTO[mesDe(fin)] ?? ""}`;
const mesCorto = (iso: string): string => `${MES_CORTO[mesDe(iso)] ?? ""} ${iso.slice(0, 4)}`;
/**
 * Un rango de días en palabras, sin fechas ISO: «31 ago 2026» (un día),
 * «sep 2026» (un mes entero), «sep 2025 – ago 2026» (meses enteros) o
 * «15 sep 2025 – 20 ago 2026» (lo demás: no se redondea a meses lo que no
 * los cubre enteros).
 */
export function etiquetaRango(inicio: string, fin: string): string {
  const i = inicio.slice(0, 10), f = fin.slice(0, 10);
  if (i === f) return diaCorto(i);
  const [fy, fm] = f.split("-").map(Number);
  const mesesEnteros = i.endsWith("-01") && f === finDeMes(fy ?? 0, (fm ?? 1) - 1);
  if (!mesesEnteros) return `${diaCorto(i)} – ${diaCorto(f)}`;
  return i.slice(0, 7) === f.slice(0, 7) ? mesCorto(i) : `${mesCorto(i)} – ${mesCorto(f)}`;
}
export const SUB_COBERTURA = "todo lo que cubre el escenario";

/** La opción «todo lo que cubre el escenario» (la tabla entera). */
export function opcionCobertura(cob: Cobertura): PeriodoOpcion {
  return { valor: `${cob.inicio}..${cob.fin}`, etiqueta: etiquetaRango(cob.inicio, cob.fin), inicio: cob.inicio, fin: cob.fin, sub: SUB_COBERTURA };
}

// ── cobertura del puerto (ADR-18) ──────────────────────────────────────

type PeriodoPuerto = NonNullable<ConceptResultWire["period"]>;
/** El periodo del puerto como rango de días: una fecha es un rango de un día. */
export const rangoDe = (p: PeriodoPuerto): { inicio: string; fin: string } =>
  p.kind === "date" ? { inicio: p.date.slice(0, 10), fin: p.date.slice(0, 10) } : { inicio: p.start.slice(0, 10), fin: p.end.slice(0, 10) };
/** «sep 2025 – ago 2026» · «31 ago 2026»: un periodo del puerto en palabras. */
export const etiquetaPeriodoPuerto = (p: PeriodoPuerto): string => { const r = rangoDe(p); return etiquetaRango(r.inicio, r.fin); };

/**
 * Lo que cubre el origen para un concepto, en la forma de la pantalla.
 * Prefiere `coverage` del puerto (la API nueva) y cae en el sobre
 * `cobertura` (la API anterior): el Studio y la API se despliegan por
 * separado. `coverage: null` es que el adaptador no la declara (una fuente
 * viva): entonces vale lo que diga el sobre, si dice algo.
 */
export function coberturaDeConcepto(disp: Pick<ConceptAvailabilityWire, "coverage"> | undefined, sobre: Cobertura | undefined): Cobertura | undefined {
  const cov = disp?.coverage;
  if (cov !== null && cov !== undefined) return { ...rangoDe(cov.period), fija: cov.fixed };
  return sobre;
}

type Cubierto = NonNullable<ConceptResultWire["covered"]>;
/**
 * Lo que dice `period_not_covered` que sí cubre, como cobertura: sin
 * ningún desglose ofrecido es una tabla que solo se sirve entera.
 */
export const coberturaDeCubierto = (cub: Cubierto): Cobertura => ({ ...rangoDe(cub.period), fija: cub.period.kind === "date" || cub.granularities.length === 0 });

/** ¿Lo pedido cae entero dentro de lo que se cubre? Si sí, el problema de `period_not_covered` es la forma (desglose, meses enteros, tabla fija), no el rango. */
export function pedidoDentro(consulta: Pick<ConsultaSimulador, "periodo" | "fecha">, cub: Cubierto): boolean {
  const pedido = rangoPedido(consulta);
  if (pedido === null) return false;
  const c = rangoDe(cub.period);
  return pedido.inicio >= c.inicio && pedido.fin <= c.fin;
}

/**
 * Por qué no se sirve lo pedido cuando el rango sí está (la forma), en
 * palabras y sin el mensaje del contrato (que trae fechas ISO e
 * identificadores: va aparte, en «Mensaje del contrato»).
 */
export function porqueForma(consulta: Pick<ConsultaSimulador, "periodo" | "fecha">, cub: Cubierto): string {
  if (coberturaDeCubierto(cub).fija) return "solo se sirve entero, sin desglose";
  if (consulta.fecha !== undefined && cub.period.kind === "period") return "no se sirve a una fecha, sino por periodo";
  const p = consulta.periodo;
  if (p !== undefined) {
    const [fy, fm] = p.fin.split("-").map(Number);
    if (!p.inicio.endsWith("-01") || p.fin !== finDeMes(fy ?? 0, (fm ?? 1) - 1)) return "solo se sirve por meses enteros";
  }
  return "no lo sirve de esa forma (el mensaje del contrato dice por qué)";
}

/**
 * Las dos mitades de «El origen no tiene {pedido}: {cubre}», también con
 * saldos: «el dato a 30 sep 2026» / «solo lo tiene a 31 ago 2026».
 * `tiene`: el arranque de la variante de forma («cubre X» / «tiene el dato a X»).
 */
export function frasesNoCubierto(consulta: Pick<ConsultaSimulador, "periodo" | "fecha">, cub: Cubierto): { pedido: string; cubre: string; tiene: string } {
  const p = rangoPedido(consulta);
  const pedido = p === null ? "datos de lo pedido" : consulta.fecha !== undefined ? `el dato a ${diaCorto(consulta.fecha)}` : `datos de ${etiquetaRango(p.inicio, p.fin)}`;
  const txt = etiquetaPeriodoPuerto(cub.period);
  if (cub.period.kind === "date") return { pedido, cubre: `solo lo tiene a ${txt}`, tiene: `tiene el dato a ${txt}` };
  return { pedido, cubre: `cubre ${txt}${coberturaDeCubierto(cub).fija ? ", y solo entero, sin desglose" : ""}`, tiene: `cubre ${txt}` };
}

/** El rango pedido por una consulta (el periodo, o la fecha como un día). */
export const rangoPedido = (consulta: Pick<ConsultaSimulador, "periodo" | "fecha">): { inicio: string; fin: string } | null =>
  consulta.periodo !== undefined ? consulta.periodo : consulta.fecha !== undefined ? { inicio: consulta.fecha, fin: consulta.fecha } : null;

/**
 * Un resultado con filas cuyo periodo servido no es el pedido: la fuente
 * lo cubría solo en parte y se sirve el recorte (ADR-18). Devuelve los dos
 * en palabras, o null si no hay recorte.
 */
export function recorte(consulta: Pick<ConsultaSimulador, "periodo" | "fecha">, r: Pick<ConceptResultWire, "status" | "period">): { pedido: string; servido: string } | null {
  if (!conFilas(r.status) || r.period === null) return null;
  const pedido = rangoPedido(consulta);
  if (pedido === null) return null;
  const servido = rangoDe(r.period);
  if (servido.inicio === pedido.inicio && servido.fin === pedido.fin) return null;
  return { pedido: etiquetaRango(pedido.inicio, pedido.fin), servido: etiquetaRango(servido.inicio, servido.fin) };
}

/** Los periodos cerrados relativos a `ancla` (hoy, o el día siguiente a la cobertura), del más reciente al más antiguo. */
function periodosRelativos(tipo: Granularidad, ancla: Date): PeriodoOpcion[] {
  const y = ancla.getFullYear(), m0 = ancla.getMonth();
  if (tipo === "month") {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(y, m0 - 1 - i, 1);
      const yy = d.getFullYear(), mm = d.getMonth();
      return { valor: `${yy}-${pad(mm + 1)}`, etiqueta: `${MESES[mm]} ${yy}`, inicio: `${yy}-${pad(mm + 1)}-01`, fin: finDeMes(yy, mm) };
    });
  }
  if (tipo === "quarter") {
    const actual = y * 4 + Math.floor(m0 / 3);
    return Array.from({ length: 4 }, (_, i) => {
      const k = actual - 1 - i, yy = Math.floor(k / 4), q = (k % 4) + 1;
      const inicio = `${yy}-${pad((q - 1) * 3 + 1)}-01`, fin = finDeMes(yy, q * 3 - 1);
      return { valor: `${yy}-Q${q}`, etiqueta: `${ordinalTrimestre(q)} ${yy}`, inicio, fin, sub: rangoMeses(inicio, fin) };
    });
  }
  const out: PeriodoOpcion[] = [];
  // en enero el año en curso aún no tiene ningún mes cerrado
  if (m0 > 0) out.push({ valor: String(y), etiqueta: `${y}, hasta ${MESES[m0 - 1]}`, inicio: `${y}-01-01`, fin: finDeMes(y, m0 - 1), sub: rangoMeses(`${y}-01-01`, finDeMes(y, m0 - 1)) });
  for (let i = 1; i <= 2; i++) out.push({ valor: String(y - i), etiqueta: String(y - i), inicio: `${y - i}-01-01`, fin: `${y - i}-12-31`, sub: "ene – dic" });
  return out;
}

/** El día siguiente a una fecha ISO, como fecha local (el ancla de los periodos con cobertura). */
const diaSiguiente = (iso: string): Date => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, (d ?? 1) + 1);
};

/** El periodo natural (mes, trimestre o año entero) que contiene una fecha local. */
function periodoNatural(tipo: Granularidad, d: Date): { inicio: string; fin: string; etiqueta: string; sub?: string } {
  const y = d.getFullYear(), m0 = d.getMonth();
  if (tipo === "month") return { inicio: `${y}-${pad(m0 + 1)}-01`, fin: finDeMes(y, m0), etiqueta: `${MESES[m0]} ${y}` };
  if (tipo === "quarter") {
    const q = Math.floor(m0 / 3) + 1, inicio = `${y}-${pad((q - 1) * 3 + 1)}-01`, fin = finDeMes(y, q * 3 - 1);
    return { inicio, fin, etiqueta: `${ordinalTrimestre(q)} ${y}`, sub: rangoMeses(inicio, fin) };
  }
  return { inicio: `${y}-01-01`, fin: `${y}-12-31`, etiqueta: String(y), sub: "ene – dic" };
}
const fechaLocal = (iso: string): Date => { const [y, m, d] = iso.split("-").map(Number); return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1); };

/**
 * Lo que se puede pedir aunque el origen no lo cubra entero (para provocar
 * `period_not_covered` y el recorte desde la pantalla, como lo pedirá el
 * asesor): el periodo natural que empieza o sigue justo tras la cobertura y
 * los que la solapan solo en parte por delante o por detrás. Marcados.
 */
function periodosMarcados(tipo: Granularidad, cob: Cobertura): PeriodoOpcion[] {
  const out: PeriodoOpcion[] = [];
  let d = diaSiguiente(cob.fin);
  for (;;) {
    const p = periodoNatural(tipo, d);
    if (p.fin < cob.inicio) break;
    const dentro = p.inicio >= cob.inicio && p.fin <= cob.fin;
    if (!dentro) {
      const marca = p.inicio > cob.fin ? "fuera" as const : "parte" as const;
      out.push({ valor: `${p.inicio}..${p.fin}`, etiqueta: p.etiqueta, inicio: p.inicio, fin: p.fin, sub: p.sub === undefined ? MARCA[marca] : `${p.sub} · ${MARCA[marca]}`, marca });
    }
    d = fechaLocal(p.inicio);
    d.setDate(d.getDate() - 1);
  }
  return out;
}

/**
 * Los periodos cerrados que se ofrecen, del más reciente al más antiguo.
 * El de por defecto es el primero sin marca (`porDefecto`).
 * - Sin cobertura: doce meses, cuatro trimestres y el año en curso hasta
 *   el último mes cerrado más los dos anteriores, relativos a hoy.
 * - Con cobertura: los mismos, anclados al día siguiente a su fin y
 *   filtrados a los que caen enteros dentro; en «Año», si la cobertura
 *   entera no coincide con ninguna opción, va la primera. Y, marcados, el
 *   periodo justo tras la cobertura («fuera de lo que cubre») y los que la
 *   solapan solo en parte («cubierto en parte»): el simulador ha de poder
 *   pedir lo que pedirá el asesor (2026-10-05).
 * - Con cobertura fija: una sola opción, la tabla entera.
 */
export function periodos(tipo: Granularidad, hoy: Date, cob?: Cobertura): PeriodoOpcion[] {
  if (cob === undefined) return periodosRelativos(tipo, hoy);
  if (cob.fija) return [opcionCobertura(cob)];
  const dentro = periodosRelativos(tipo, diaSiguiente(cob.fin)).filter((o) => o.inicio >= cob.inicio && o.fin <= cob.fin);
  const cubiertos = tipo === "year" && !dentro.some((o) => o.inicio === cob.inicio && o.fin === cob.fin) ? [opcionCobertura(cob), ...dentro] : dentro;
  // los marcados, en su sitio por fecha de fin (sort es estable: los cubiertos conservan su orden)
  return [...cubiertos, ...periodosMarcados(tipo, cob)].sort((a, b) => (a.fin < b.fin ? 1 : a.fin > b.fin ? -1 : 0));
}
/** La opción por defecto de una lista: la primera que el origen cubre entera. */
export const porDefecto = (ops: readonly PeriodoOpcion[]): PeriodoOpcion | undefined => ops.find((o) => o.marca === undefined) ?? ops[0];
/** ¿Tiene este tipo algún periodo que el origen cubra entero? */
const conCubiertos = (tipo: Granularidad, hoy: Date, cob?: Cobertura): boolean => periodos(tipo, hoy, cob).some((o) => o.marca === undefined);

const NINGUNO: Readonly<Record<Granularidad, string>> = { month: "ningún mes entero", quarter: "ningún trimestre entero", year: "ningún año entero" };
/** El porqué de un tipo de periodo apagado («el escenario no cubre ningún trimestre entero»), o null si tiene opciones. */
export function porqueSinPeriodo(tipo: Granularidad, hoy: Date, cob?: Cobertura): string | null {
  return periodos(tipo, hoy, cob).length > 0 ? null : `el escenario no cubre ${NINGUNO[tipo]}`;
}

/** La fecha por defecto de un saldo: el último día del último mes cerrado; con cobertura, el final de lo que cubre. */
export const fechaPorDefecto = (hoy: Date, cob?: Cobertura): string => (cob !== undefined ? cob.fin : finDeMes(hoy.getFullYear(), hoy.getMonth() - 1));

const RANGO: Readonly<Record<Granularidad, number>> = { month: 0, quarter: 1, year: 2 };
/** ¿Cabe este desglose en este tipo de periodo? Nunca más grueso que el periodo. */
export const desgloseCabe = (g: Granularidad, tipo: Granularidad): boolean => RANGO[g] <= RANGO[tipo];
const EN_UN: Readonly<Record<Granularidad, string>> = { month: "un mes", quarter: "un trimestre", year: "un año" };
/** El globo de un desglose apagado: en qué no cabe («Más grueso que el periodo: año no cabe en un trimestre»). */
export const porqueDesglose = (g: Granularidad, tipo: Granularidad): string => `Más grueso que el periodo: ${GRAN[g].toLowerCase()} no cabe en ${EN_UN[tipo]}`;
/** «Mes», «Trimestre y Año», «Mes, Trimestre y Año»: los nombres de las píldoras, en una frase. */
export const listaGran = (gs: readonly Granularidad[]): string => {
  const n = gs.map((g) => GRAN[g]);
  return n.length <= 1 ? (n[0] ?? "") : `${n.slice(0, -1).join(", ")} y ${n.at(-1) ?? ""}`;
};
/**
 * Al cambiar el tipo de periodo, el desglose que ya no cabe pasa al del
 * tipo nuevo (si el concepto lo admite; si no, sin desglose).
 */
export function ajustarDesglose(g: Granularidad | null, tipo: Granularidad, admitidas: readonly Granularidad[]): Granularidad | null {
  if (g === null || desgloseCabe(g, tipo)) return g;
  return admitidas.includes(tipo) ? tipo : null;
}

/** «sep 2026» · «3.er trimestre 2026» · «2026»: la etiqueta de un cubo temporal de `ConceptRow.period`. */
export function etiquetaCubo(p: string): string {
  const mes = /^(\d{4})-(\d{2})$/.exec(p);
  if (mes) return `${MES_CORTO[Number(mes[2]) - 1] ?? mes[2]} ${mes[1]}`;
  const tri = /^(\d{4})-Q([1-4])$/.exec(p);
  if (tri) return `${ordinalTrimestre(Number(tri[2]))} ${tri[1]}`;
  return p;
}
/** «30 sep 2026»: una fecha ISO (día) en corto. */
export function diaCorto(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${d ?? ""} ${MES_CORTO[(m ?? 1) - 1] ?? ""} ${y ?? ""}`;
}
/** «28 sep 2026, 04:00»: el `dataDate` (instante ISO) en la hora de quien mira; el ISO crudo si no se entiende. */
export function fechaDato(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return iso.length <= 10 ? diaCorto(iso) : `${diaCorto(isoLocal(d))}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export const horaCorta = (d: Date): string => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// ── la consulta ────────────────────────────────────────────────────────

export interface Controles {
  tipo: Granularidad;
  /** `valor` de un PeriodoOpcion del tipo elegido. */
  periodo: string;
  /** YYYY-MM-DD (saldos). */
  fecha: string;
  granularidad: Granularidad | null;
  dimensiones: string[];
}

/**
 * Los controles con los que se abre un concepto: el periodo más reciente
 * que el origen cubre (con cobertura, el último mes del escenario; sin
 * ella, el último mes cerrado) y el desglose mensual si cabe.
 */
export function controlesIniciales(c: Pick<ConceptoCatalogo, "id" | "temporal" | "granularidades">, ofrecidas: readonly string[], hoy: Date, cob?: Cobertura): Controles {
  const preferido: Granularidad = c.temporal === "flow" && !c.granularidades.includes("month") && c.granularidades.includes("quarter") ? "quarter" : "month";
  // con cobertura, el primer tipo que tenga alguna opción (un escenario de un trimestre no tiene años… salvo el suyo)
  const tipo = conCubiertos(preferido, hoy, cob)
    ? preferido
    : ((["month", "quarter", "year"] as const).find((t) => conCubiertos(t, hoy, cob)) ?? preferido);
  const gran: Granularidad | null = c.temporal === "flow" && c.granularidades.includes("month") ? "month" : null;
  return {
    tipo,
    periodo: porDefecto(periodos(tipo, hoy, cob))?.valor ?? "",
    fecha: fechaPorDefecto(hoy, cob),
    granularidad: cob?.fija === true ? null : gran,
    dimensiones: (DIMS_POR_DEFECTO[c.id] ?? []).filter((d) => ofrecidas.includes(d)),
  };
}

/** El periodo efectivo de los controles (el primero del tipo si el elegido ya no está). */
export function periodoElegido(ctl: Controles, hoy: Date, cob?: Cobertura): PeriodoOpcion | undefined {
  const ops = periodos(ctl.tipo, hoy, cob);
  return ops.find((o) => o.valor === ctl.periodo) ?? porDefecto(ops);
}

/**
 * De los controles a la consulta del contrato. Saldo → `fecha`; flujo →
 * `periodo` y, si el concepto la admite y cabe en el periodo,
 * `granularidad` (con cobertura fija, nunca: la tabla no se desglosa). Las
 * dimensiones solo viajan si hay alguna: una lista vacía y ninguna dicen
 * lo mismo.
 */
export function construirConsulta(c: Pick<ConceptoCatalogo, "id" | "temporal" | "granularidades">, ctl: Controles, hoy: Date, cob?: Cobertura): ConsultaSimulador {
  const dims = ctl.dimensiones.length > 0 ? { dimensiones: [...ctl.dimensiones] } : {};
  if (c.temporal === "balance") return { concepto: c.id, fecha: ctl.fecha, ...dims };
  const p = periodoElegido(ctl, hoy, cob);
  const g = ctl.granularidad;
  const gran = g !== null && cob?.fija !== true && c.granularidades.includes(g) && desgloseCabe(g, ctl.tipo) ? { granularidad: g } : {};
  return { concepto: c.id, periodo: { inicio: p?.inicio ?? "", fin: p?.fin ?? "" }, ...gran, ...dims };
}

/**
 * Los controles de «pedir lo que sí cubre» (`period_not_covered`): los
 * mismos, movidos a lo que el origen cubre (`cob`, la cobertura con la
 * que se ofrecen los periodos). Un saldo, a la última fecha cubierta; un
 * flujo, al periodo más reciente cubierto del mismo tipo (o del primer tipo
 * que tenga alguno), como la consulta por defecto. El desglose se queda si
 * el origen lo ofrece y cabe; si no, el del tipo o ninguno. Las dimensiones
 * no se tocan: no son lo que falló.
 */
export function controlesCubiertos(c: Pick<ConceptoCatalogo, "temporal">, ctl: Controles, cob: Cobertura, ofrecidas: readonly Granularidad[], hoy: Date): Controles {
  if (c.temporal === "balance") return { ...ctl, fecha: cob.fin };
  // solo lo que cubre entero: lo marcado es justo lo que no se sirve
  const tipo = conCubiertos(ctl.tipo, hoy, cob)
    ? ctl.tipo
    : ((["month", "quarter", "year"] as const).find((t) => conCubiertos(t, hoy, cob)) ?? ctl.tipo);
  const g = ajustarDesglose(ctl.granularidad, tipo, ofrecidas);
  return { ...ctl, tipo, periodo: porDefecto(periodos(tipo, hoy, cob))?.valor ?? "", granularidad: cob.fija || g === null || !ofrecidas.includes(g) ? null : g };
}

// ── dimensiones ────────────────────────────────────────────────────────

export interface DimensionOferta { dim: string; ofrecida: boolean }

/**
 * Las dimensiones del concepto, cada una diciendo si la receta la ofrece.
 * Sin disponibilidad (el origen no sirve el concepto) ninguna se ofrece;
 * con la receta editada, todas (lo comprueba la API al ejecutar).
 */
export function dimensionesDe(c: Pick<ConceptoCatalogo, "dimensiones">, disp: Pick<ConceptAvailabilityWire, "dimensions"> | undefined, recetaEditada: boolean): DimensionOferta[] {
  return c.dimensiones.map((dim) => ({ dim, ofrecida: recetaEditada || (disp?.dimensions.includes(dim) ?? false) }));
}

/**
 * Lo escrito en el campo de dimensiones → la dimensión a añadir, o el
 * porqué de no añadirla (el error en línea bajo el campo). Se acepta la
 * etiqueta («Cliente») o el identificador (`customer`), sin mayúsculas.
 */
export function leerDimension(texto: string, c: Pick<ConceptoCatalogo, "etiqueta" | "dimensiones">, oferta: readonly DimensionOferta[]): { dim: string } | { error: string } {
  const q = texto.trim().toLowerCase();
  const dim = Object.keys(DIM).find((k) => k === q || (DIM[k] ?? "").toLowerCase() === q) ?? (c.dimensiones.includes(q) ? q : undefined);
  const admite = c.dimensiones.length > 0 ? c.dimensiones.map(dimEtiqueta).join(", ") : "ninguna";
  if (dim === undefined) return { error: `«${texto.trim()}» no es una dimensión del catálogo. ${c.etiqueta} admite: ${admite}.` };
  if (!c.dimensiones.includes(dim)) return { error: `${c.etiqueta} no admite la dimensión «${dimEtiqueta(dim)}». Admite: ${admite}.` };
  if (!oferta.find((o) => o.dim === dim)?.ofrecida) return { error: `La receta no ofrece «${dimEtiqueta(dim)}»: no se puede pedir hasta que la receta la sirva.` };
  return { dim };
}

/** Para el enlace «quitar X y ejecutar» de `missing_dimension`: la primera pedida que la receta no ofrece. */
export function dimensionQueFalta(pedidas: readonly string[], disp: Pick<ConceptAvailabilityWire, "dimensions"> | undefined): string | null {
  return pedidas.find((d) => !(disp?.dimensions.includes(d) ?? false)) ?? null;
}

// ── la receta ──────────────────────────────────────────────────────────

/**
 * ¿El texto del editor es distinto del de referencia? Sin referencia (el
 * concepto no tiene receta: se parte de la plantilla) cualquier texto es
 * ya una receta editada.
 */
export function esEditada(texto: string | undefined, base: string | null): boolean {
  if (texto === undefined) return false;
  return base === null ? texto.trim() !== "" : texto !== base;
}
/** Dónde vive (o viviría) la receta en el repo. */
export const rutaReceta = (tipo: string, concepto: string): string => `content/connectors/recipes/${tipo}/${concepto}.yaml`;

/**
 * Plantilla de una receta de Business Central POR ESCRIBIR (la del
 * prototipo): incompleta a propósito —implements, entidad y valor—, así
 * que la API la rechaza con su porqué hasta que se rellena.
 */
export function plantilla(c: Pick<ConceptoCatalogo, "id" | "etiqueta" | "temporal" | "version">): string {
  const flujo = c.temporal === "flow";
  return `# Receta por escribir para «${c.etiqueta}». Antes de ejecutarla: di en
# implements qué definición implementa (20 caracteres como mínimo), elige
# la entidad de la API v2.0 y el campo del valor.
format: 1
concept: ${c.id}
concept_version: ${c.version}
source_type: business-central
scope: standard
version: 1
status: draft
implements: ""
request:
  entity: ""
  filter: "${flujo ? "postingDate ge {{period.start}} and postingDate le {{period.end}}" : "postingDate le {{date}}"}"
normalize:
  rows: value
${flujo ? "  period: { field: postingDate }\n" : ""}  value: ""
  operations:
    - { op: round, decimals: 2 }
`;
}

// ── estados del resultado ──────────────────────────────────────────────

export const ESTADO: Readonly<Record<Estado, { etiqueta: string; tono: Tono }>> = {
  ok: { etiqueta: "ok", tono: "ok" },
  stale: { etiqueta: "dato viejo", tono: "warn" },
  unsupported_concept: { etiqueta: "no disponible", tono: undefined },
  missing_dimension: { etiqueta: "falta una dimensión", tono: "warn" },
  permission_denied: { etiqueta: "sin permiso", tono: "danger" },
  connection_expired: { etiqueta: "conexión caducada", tono: "danger" },
  source_error: { etiqueta: "error de la fuente", tono: "danger" },
  period_not_covered: { etiqueta: "periodo no cubierto", tono: "warn" },
};

const FALLO_FRESCURA: Readonly<Record<string, string>> = {
  connection_expired: "la conexión ha caducado", permission_denied: "la fuente no dio permiso", source_error: "la fuente falló",
};
/**
 * El porqué de no saber de cuándo es el dato, si fue un fallo de la
 * conexión (`frescura.estado`, ADR-19): «no se pudo saber: la conexión ha
 * caducado». null si no hubo fallo (o la API anterior no lo dice): entonces
 * el «sin fecha» es que el adaptador no la sabe.
 */
export function falloFrescura(f: { estado?: string | undefined }): string | null {
  if (f.estado === undefined || f.estado === "ok") return null;
  return `no se pudo saber: ${FALLO_FRESCURA[f.estado] ?? "la fuente falló"}`;
}
/** La insignia del último resultado en la lista: «ok · 4 filas» o la etiqueta del estado. */
export function chipUltimo(status: Estado, filas: number): { texto: string; tono: Tono } {
  const e = ESTADO[status];
  return status === "ok" ? { texto: `ok · ${filas} ${filas === 1 ? "fila" : "filas"}`, tono: e.tono } : { texto: e.etiqueta, tono: e.tono };
}
export const conFilas = (s: Estado): boolean => s === "ok" || s === "stale";

// ── formato de las cifras ──────────────────────────────────────────────

/* `useGrouping: "always"`: en es-ES la agrupación por defecto no separa los
   miles de un número de cuatro cifras («4200,00»), y en una columna de
   importes descuadra. El valor es estándar (ES2023) pero el `lib` de
   TypeScript del Studio aún lo tipa como booleano: de ahí el molde. */
const SIEMPRE = { useGrouping: "always" } as unknown as Pick<Intl.NumberFormatOptions, "useGrouping">;
const NF = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2, ...SIEMPRE });
const NF_LIBRE = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 6, ...SIEMPRE });
/** El valor de una fila con su unidad: los importes con dos decimales y su moneda («€» si es EUR). */
export function formatoValor(v: number, unit: ConceptResultWire["unit"], currency: string | undefined): string {
  if (unit === "currency") return `${NF.format(v)} ${currency === undefined || currency === "EUR" ? "€" : currency}`;
  if (unit === "percentage") return `${NF_LIBRE.format(v)} %`;
  if (unit === "days") return `${NF_LIBRE.format(v)} días`;
  return NF_LIBRE.format(v);
}
export function unidadTexto(unit: ConceptResultWire["unit"], currency: string | undefined, sign: ConceptResultWire["sign"]): string {
  const u = unit === "currency" ? `importe${currency ? ` en ${currency}` : " (sin moneda: la fuente no la dijo)"}` : unit === "percentage" ? "porcentaje" : unit === "days" ? "días" : "recuento";
  return `${u} · ${sign === "magnitude" ? "nunca negativo" : "con signo"}`;
}

// ── la conexión ────────────────────────────────────────────────────────

/** Sin client id no hay app de Entra que pedir: Business Central queda apagado con porqué. */
export const faltaConfigurar = (clientId: string | undefined): boolean => (clientId ?? "").trim() === "";
/** El porqué de «falta configurar», en prosa; la variable y la app van aparte, como etiquetas de máquina. */
export const AVISO_FALTA_CONFIG = "Falta configurar la app de Entra del simulador.";
export const VARIABLE_CLIENT_ID = "VITE_SIMULADOR_CLIENT_ID";
export const APP_ENTRA = "appreg-skein-simulador";

/**
 * El código técnico de un fallo de la ventana de Microsoft (`errorCode` de
 * MSAL: `popup_window_error`, `interaction_in_progress`…), para enseñarlo
 * como etiqueta de máquina bajo un mensaje en español; null si no lo trae.
 */
export function codigoConexion(e: unknown): string | null {
  const c = typeof e === "object" && e !== null ? (e as { errorCode?: unknown }).errorCode : undefined;
  return typeof c === "string" && c.trim() !== "" ? c : null;
}

/** Los enums del entorno de Business Central (vienen en inglés) en palabras de quien mira. */
const TIPO_ENTORNO: Readonly<Record<string, string>> = { production: "producción", sandbox: "sandbox (copia de pruebas)" };
const ESTADO_ENTORNO: Readonly<Record<string, string>> = {
  active: "activo", preparing: "preparándose", upgrading: "actualizándose", removing: "eliminándose", softdeleted: "eliminado",
  "soft deleted": "eliminado", suspended: "suspendido", copying: "copiándose", restoring: "restaurándose", inactive: "inactivo",
};
const REGION = (() => { try { return new Intl.DisplayNames(["es"], { type: "region" }); } catch { return null; } })();
const pais = (c: string): string => {
  if (!/^[A-Za-z]{2}$/.test(c)) return c;
  try { return REGION?.of(c.toUpperCase()) ?? c; } catch { return c; }
};
/** «producción · España · activo» · «escrito a mano»: la sublínea de un entorno en el select. */
export function etiquetaEntorno(e: Pick<EntornoBC, "tipo" | "pais" | "estado" | "procedencia">): string | undefined {
  if (e.procedencia === "manual") return "escrito a mano";
  const t = e.tipo === null ? null : (TIPO_ENTORNO[e.tipo.toLowerCase()] ?? e.tipo);
  const s = e.estado === null ? null : (ESTADO_ENTORNO[e.estado.toLowerCase()] ?? e.estado);
  return [t, e.pais === null ? null : pais(e.pais), s].filter((x): x is string => x !== null && x !== "").join(" · ") || undefined;
}

/**
 * Dónde se enseña un fallo de «ejecutar»: la receta editada que no vale
 * (`invalid-recipe`, bajo el editor), la consulta que no vale
 * (`invalid-query`, bajo «Ejecutar») o el resto, en la card del resultado.
 * «Reintentar» solo tiene sentido si repetir puede salir distinto: un fallo
 * de red o del servidor, no un 4xx (la misma petición dará lo mismo).
 */
export type FalloEjecutar =
  | { donde: "receta" | "consulta"; msg: string }
  | { donde: "resultado"; msg: string | null; reintentar: boolean };
export function clasificarFallo(e: unknown): FalloEjecutar {
  if (!(e instanceof ApiError)) return { donde: "resultado", msg: null, reintentar: true };
  if (e.code === "invalid-recipe") return { donde: "receta", msg: e.message };
  if (e.code === "invalid-query") return { donde: "consulta", msg: e.message };
  return { donde: "resultado", msg: e.message, reintentar: !(e.status >= 400 && e.status < 500) };
}

/** «CRONUS ES · Production»: el origen de Business Central como se nombra en la pantalla. */
export const etiquetaOrigenBC = (empresa: string, entorno: string): string => `${empresa} · ${entorno}`;
const MONEDA = /^[A-Z]{3}$/;
/** La moneda de la empresa solo viaja si es un código ISO: el contrato la rechaza si no. */
export const monedaValida = (m: string | null): string | null => (m !== null && MONEDA.test(m) ? m : null);
